import {
  GenericFullscreenDialog,
  Button,
  FileCard,
  ArtemSpinner,
  Alert,
  ProgressIndicator
} from '@/common'
import {
  importedGoodsGRRErrors,
  importedGoodsGRRFields,
  useProcessImportedGoodsGRR
} from '@/features/cbam-reporting/hooks/importedGoodsGRR'
import type { ImportedGoodsGRRProcessResult } from '@/features/cbam-reporting/hooks/importedGoodsGRR'
import { useFinalizeGrrImportedGoods } from '@/features/cbam-reporting/hooks/importedGoods'
import React, { useState, useRef, useMemo, useCallback } from 'react'
import toast from 'react-hot-toast'
import { DocumentTextIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { GoodsReceivedReportValidationErrorsList } from '@/features/cbam-reporting/components/GoodsReceivedReportValidationErrorsList'
import { motion, AnimatePresence } from 'motion/react'
import ImportedGoodsUploadSummary from './ImportedGoodsUploadSummary'
import ImportedGoodsUploadGuidelines from './ImportedGoodsUploadGuidelines'
import useI18n from '@/shared/hooks/usei18n'
import createCsvAndDownload from '@/shared/utils/createCsvAndDownload'
import { Trans } from 'react-i18next'

// Step definitions
const STEPS = {
  UPLOAD: 0,
  VALIDATION: 1,
  SUMMARY: 2
} as const

type Step = (typeof STEPS)[keyof typeof STEPS]

// Template for CSV download
const IMPORTED_GOODS_TEMPLATE = [
  {
    'MRN No.': '',
    Date: '',
    'HS Code': '',
    'Country of Origin': '',
    'Net Weight': '',
    Seller: '',
    'Unique Import ID CBAM': '',
    'Customs Rule': '',
    'Description of the goods': ''
  }
]

function createTemplateAndDownload() {
  createCsvAndDownload('imported-goods-grr-template.csv', IMPORTED_GOODS_TEMPLATE)
}

export default function ImportedGoodsUploadDialog({
  isOpen,
  close,
  organizationId,
  cbamReportId,
  reportingPeriodYear,
  isRepresentative
}: {
  isOpen: boolean
  close: () => void
  organizationId: string
  cbamReportId: string
  reportingPeriodYear: number
  isRepresentative: boolean
}) {
  const { t } = useI18n()
  const [currentStep, setCurrentStep] = useState<Step>(STEPS.UPLOAD)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [processResult, setProcessResult] = useState<ImportedGoodsGRRProcessResult | null>(null)
  const [isCreatingGoods, setIsCreatingGoods] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { mutateAsync: processGRR, isPending } = useProcessImportedGoodsGRR(
    organizationId,
    cbamReportId
  )
  const { mutateAsync: finalizeGrrImportedGoods } = useFinalizeGrrImportedGoods(
    organizationId,
    cbamReportId
  )

  const validateFile = (file: File): boolean => {
    const validExtensions = ['.csv', '.xlsx']
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

    if (!validExtensions.includes(fileExtension)) {
      toast.error(t('cbamReporting:importedGoodsUpload.errors.invalidFileType'))
      return false
    }

    const maxSizeInMB = 15
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024
    if (file.size > maxSizeInBytes) {
      toast.error(t('cbamReporting:importedGoodsUpload.errors.fileTooLarge', { size: maxSizeInMB }))
      return false
    }

    return true
  }

  const handleFileSelect = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0]
    if (file && validateFile(file)) {
      setSelectedFile(file)
    }
  }

  const handleDragOver = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
    setIsDragging(false)

    const file = evt.dataTransfer.files?.[0]
    if (file && validateFile(file)) {
      setSelectedFile(file)
    }
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setProcessResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetToUpload = () => {
    setCurrentStep(STEPS.UPLOAD)
    setProcessResult(null)
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetDialogState = useCallback(() => {
    setCurrentStep(STEPS.UPLOAD)
    setSelectedFile(null)
    setProcessResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(t('cbamReporting:importedGoodsUpload.errors.noFileSelected'))
      return
    }

    // Detect file format from extension
    const fileExtension = selectedFile.name
      .toLowerCase()
      .substring(selectedFile.name.lastIndexOf('.'))
    const fileFormat: 'csv' | 'xlsx' = fileExtension === '.xlsx' ? 'xlsx' : 'csv'

    try {
      const result = await processGRR({
        file: selectedFile,
        fileFormat
      })

      setProcessResult(result)

      // Move to validation step after processing
      setCurrentStep(STEPS.VALIDATION)

      if (result.stats.validRows === 0) {
        toast.error(t('cbamReporting:importedGoodsUpload.errors.noValidRows'))
        return
      }

      // If no errors, auto-proceed to summary
      if (result.errors.length === 0 && result.stats.validRows > 0) {
        setCurrentStep(STEPS.SUMMARY)
      }

      toast.success(
        t('cbamReporting:importedGoodsUpload.notifications.processed', {
          count: result.stats.validRows
        })
      )
    } catch (error: any) {
      if (error?.response?.status === 413) {
        toast.error(t('cbamReporting:importedGoodsUpload.errors.payloadTooLarge'))
      } else {
        toast.error(t('cbamReporting:importedGoodsUpload.errors.processingFailed'))
      }
    }
  }

  const handleProceedToSummary = () => {
    setCurrentStep(STEPS.SUMMARY)
  }

  const handleBackToValidation = () => {
    setCurrentStep(STEPS.VALIDATION)
  }

  const goodsToCreate = useMemo(() => {
    if (!processResult) {
      return []
    }
    return processResult.data
  }, [processResult])

  const liabilityDataForYear = useMemo(() => {
    const preview = processResult?.liabilityPreview
    if (!preview) {
      return undefined
    }
    return (
      preview.aggregatedProjection[reportingPeriodYear] ||
      preview.aggregatedProjection[2026]
    )
  }, [processResult?.liabilityPreview, reportingPeriodYear])

  const isLiabilityPreviewMissing = useMemo(
    () => processResult != null && processResult.liabilityPreview === null,
    [processResult]
  )

  const liabilityTotals = useMemo(() => {
    if (!processResult) {
      return {
        weight: 0,
        directEmissions: 0 as number | null,
        indirectEmissions: 0 as number | null
      }
    }
    if (processResult.liabilityPreview === null) {
      const weight = goodsToCreate.reduce((sum, row) => sum + row.netMassInTonnes, 0)
      return {
        weight,
        directEmissions: null,
        indirectEmissions: null
      }
    }
    return {
      weight: liabilityDataForYear?.volume ?? 0,
      directEmissions: liabilityDataForYear?.directEmissions ?? 0,
      indirectEmissions: liabilityDataForYear?.indirectEmissions ?? 0
    }
  }, [processResult, goodsToCreate, liabilityDataForYear])

  const itemsWithEmissions = useMemo(() => {
    if (!processResult) {
      return []
    }
    if (processResult.liabilityPreview === null) {
      return goodsToCreate.map(row => ({
        item: row,
        directEmissions: null as number | null,
        indirectEmissions: null as number | null
      }))
    }
    const results = processResult.liabilityPreview?.results
    if (!results?.length) {
      return goodsToCreate.map(row => ({
        item: row,
        directEmissions: 0,
        indirectEmissions: 0
      }))
    }
    return goodsToCreate.map((row, index) => {
      const result = results[index]
      const projectionYear =
        result?.result?.projection[reportingPeriodYear] || result?.result?.projection[2026]
      return {
        item: row,
        directEmissions: projectionYear?.specificDirectEmbeddedEmissions ?? 0,
        indirectEmissions: projectionYear?.specificIndirectEmbeddedEmissions ?? 0
      }
    })
  }, [goodsToCreate, processResult, reportingPeriodYear])

  const handleCreateAllGoods = useCallback(async () => {
    if (!processResult?.goodsReceivedReportId) {
      toast.error(t('cbamReporting:importedGoodsUpload.notifications.error'))
      return
    }

    if (itemsWithEmissions.length === 0) {
      return
    }

    setIsCreatingGoods(true)

    try {
      const result = await finalizeGrrImportedGoods(processResult.goodsReceivedReportId)

      if (result.errors.length > 0) {
        toast.error(
          t('cbamReporting:importedGoodsUpload.notifications.partialSuccess', {
            created: result.created.length,
            failed: result.errors.length
          })
        )
      } else {
        toast.success(
          t('cbamReporting:importedGoodsUpload.notifications.success', {
            count: result.created.length
          })
        )
      }

      close()
      resetDialogState()
    } catch {
      toast.error(t('cbamReporting:importedGoodsUpload.notifications.error'))
    } finally {
      setIsCreatingGoods(false)
    }
  }, [itemsWithEmissions, finalizeGrrImportedGoods, t, processResult, close, resetDialogState])

  const errors = useMemo(
    () =>
      processResult?.errors
        .filter(error => {
          const grrError = importedGoodsGRRErrors[error.key]
          if (grrError) {
            return grrError.isBlockingEvaluation
          }
          // Fallback: treat untracked errors as blocking errors
          return true
        })
        .map(error => {
          return { ...error, fieldName: importedGoodsGRRFields[error.field] }
        }) ?? [],
    [processResult?.errors]
  )

  const warnings = useMemo(
    () =>
      processResult?.errors
        .filter(error => {
          const grrError = importedGoodsGRRErrors[error.key]
          if (grrError) {
            return !grrError.isBlockingEvaluation
          }
          // Untracked errors are handled as blocking errors, not warnings
          return false
        })
        .map(error => {
          return { ...error, fieldName: importedGoodsGRRFields[error.field] }
        }) ?? [],
    [processResult?.errors]
  )

  const handleClose = () => {
    if (isCreatingGoods) {
      return
    }

    close()
    resetDialogState()
  }

  const isProceedToSummaryDisabled = useMemo(() => {
    return (
      !processResult ||
      processResult.data.length === 0 ||
      errors.some(error => error.key === 'CUSTOM_ID_DUPLICATE_DIFFERENT_DATE')
    )
  }, [processResult, errors])

  const renderUploadStep = () => (
    <div className="flex flex-col gap-6 pb-8">
      <div>
        <h2 className="text-xl font-semibold">{t('cbamReporting:importedGoodsUpload.title')}</h2>
        <p className="text-sm text-text-secondary mt-2">
          <Trans
            i18nKey="cbamReporting:importedGoodsUpload.description"
            components={{
              Link: (
                <button
                  className="text-green-600 underline cursor-pointer"
                  onClick={() => createTemplateAndDownload()}
                />
              )
            }}
          />
        </p>
      </div>

      <ImportedGoodsUploadGuidelines isRepresentative={isRepresentative} />

      <div className="flex flex-col items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isPending}
        />

        {!selectedFile ? (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            className={cx(
              'w-full border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all',
              isDragging
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-400 bg-gray-50 hover:border-primary-400 hover:bg-primary-25 ',
              isPending && 'opacity-50 cursor-not-allowed pointer-events-none'
            )}
          >
            <div className="flex flex-col items-center gap-3">
              <DocumentTextIcon
                className={cx('w-12 h-12', isDragging ? 'text-green-500' : 'text-gray-500')}
              />
              <div>
                <p className="text-base font-medium text-gray-700">
                  {isDragging
                    ? t('cbamReporting:importedGoodsUpload.dropHere')
                    : t('cbamReporting:importedGoodsUpload.dragAndDrop')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t('cbamReporting:importedGoodsUpload.browseFiles')}
                </p>
              </div>

              <p className="text-xs text-gray-400 mt-2">
                {t('cbamReporting:importedGoodsUpload.fileRequirements')}
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <FileCard
              file={{
                name: selectedFile.name,
                size: selectedFile.size,
                type: selectedFile.name.toLowerCase().endsWith('.xlsx') ? 'xlsx' : 'csv'
              }}
              onRemove={handleRemoveFile}
            />
          </div>
        )}

        <AnimatePresence mode="wait">
          {isPending && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="w-full flex flex-col items-center gap-3 mt-6 py-8"
            >
              <ArtemSpinner className="w-12 h-12" />
              <p className="text-sm text-gray-600">
                {t('cbamReporting:importedGoodsUpload.processingMessage')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Cancel button */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <Button variant="neutral" onClick={handleClose}>
          {t('cbamReporting:importedGoodsUpload.cancelButton')}
        </Button>
        <Button onClick={handleUpload} disabled={!selectedFile || isPending}>
          {t('cbamReporting:importedGoodsUpload.uploadButton')}
        </Button>
      </div>
    </div>
  )

  const renderValidationStep = () => (
    <div className="flex flex-col gap-6 pb-8">
      <div>
        <h2 className="text-xl font-semibold">
          {t('cbamReporting:importedGoodsUpload.validation.title')}
        </h2>
        <p className="text-sm text-text-secondary mt-2">
          {t('cbamReporting:importedGoodsUpload.validation.description')}
        </p>
      </div>

      {/* Stats summary */}
      {processResult && (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{selectedFile?.name}</span>
          </div>
          <p className="text-sm text-gray-600">
            {t('cbamReporting:importedGoodsUpload.validation.stats', {
              total: processResult.stats.totalRows,
              valid: processResult.stats.validRows,
              errors: processResult.stats.errorRows
            })}
          </p>
        </div>
      )}

      {/* No valid data alert */}
      {processResult && processResult.data.length === 0 && (
        <Alert type="error">
          <p className="font-semibold text-red-900">
            {t('cbamReporting:importedGoodsUpload.noDataFound.title')}
          </p>
          <p className="text-sm text-red-800 mt-1">
            {t('cbamReporting:importedGoodsUpload.noDataFound.description')}
          </p>
        </Alert>
      )}

      {/* Validation Errors Display */}
      <AnimatePresence>
        {errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <GoodsReceivedReportValidationErrorsList items={errors} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Validation Warnings Display */}
      <AnimatePresence>
        {warnings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <GoodsReceivedReportValidationErrorsList type="warning" items={warnings} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action buttons */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <div className="flex gap-2">
          <Button variant="neutral" onClick={handleClose}>
            {t('cbamReporting:importedGoodsUpload.cancelButton')}
          </Button>
          <Button variant="subtle" onClick={resetToUpload}>
            {t('cbamReporting:importedGoodsUpload.validation.uploadDifferentFile')}
          </Button>
        </div>
        <Button onClick={handleProceedToSummary} disabled={isProceedToSummaryDisabled}>
          {t('cbamReporting:importedGoodsUpload.proceedButton')}
        </Button>
      </div>
    </div>
  )

  const renderSummaryStep = () => (
    <>
      {processResult && (
        <ImportedGoodsUploadSummary
          data={goodsToCreate}
          itemsWithEmissions={itemsWithEmissions}
          totals={liabilityTotals}
          isLoadingLiability={false}
          liabilityPreviewUnavailable={isLiabilityPreviewMissing}
          isRepresentative={isRepresentative}
          onBack={handleBackToValidation}
          onCreateAll={handleCreateAllGoods}
          onCancel={handleClose}
          isCreating={isCreatingGoods}
          isCreateDisabled={!processResult.goodsReceivedReportId}
          reportingPeriodYear={reportingPeriodYear}
        />
      )}
    </>
  )

  return (
    <GenericFullscreenDialog isOpen={isOpen} close={handleClose} containerSize="5xl">
      <div className="pt-16">
        <ProgressIndicator totalSteps={3} currentStep={currentStep} />
        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={`step-${currentStep}`}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                duration: 0.5
              }}
            >
              {currentStep === STEPS.UPLOAD && renderUploadStep()}
              {currentStep === STEPS.VALIDATION && renderValidationStep()}
              {currentStep === STEPS.SUMMARY && renderSummaryStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </GenericFullscreenDialog>
  )
}
